import { Box, Breadcrumbs, Link, Typography } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Home as HomeIcon } from "@mui/icons-material";
import { Org } from "~/lib/types";
import { setDocumentTitle } from "~/lib/commonUtils";
import { useCanSetOrganizationMembership } from "~/lib/iam/actions/mondoo/captain/Captain/SetOrganizationMembership";
import { useCanRemoveOrganizationMembership } from "~/lib/iam/actions/mondoo/captain/Captain/RemoveOrganizationMembership";
import { useUserProfile } from "./hooks";
import {
  UserHeader,
  UserProfileCard,
  SpaceAccessTable,
  RemoveUserDialog,
  EditUsersDialog,
  EditOrganizationalRoleDialog,
  EditSpaceRoleDialog,
  AddSpaceRoleDialog,
  UserProfileSkeleton,
} from "./components";

export type UserProfilePageProps = {
  org: Org;
};

export function UserProfilePage({ org }: UserProfilePageProps) {
  const { userMrn } = useParams<{ userMrn: string }>();

  // Permission hooks
  const canSetMembership = useCanSetOrganizationMembership();
  const canRemoveMembership = useCanRemoveOrganizationMembership();

  const {
    user,
    loading,
    filteredSpaces,
    spaceSearch,
    setSpaceSearch,
    spacesPaginationModel,
    onSpacesPaginationModelChange,
    spacesRowCount,
    isRemoveDialogOpen,
    handleRemoveUser,
    handleConfirmRemove,
    handleCancelRemove,
    isEditUsersDialogOpen,
    handleEditUsers,
    handleSaveEditUsers,
    handleCancelEditUsers,
    isEditOrgRoleDialogOpen,
    handleEditOrgRole,
    handleSaveOrgRole,
    handleCancelEditOrgRole,
    isEditRoleLoading,
    handleRemoveSpaceAccess,
    editSpaceRoleDialog,
    handleOpenEditSpaceRole,
    handleCloseEditSpaceRole,
    handleSaveSpaceRole,
    isSaveSpaceRoleLoading,
    addSpaceDialog,
    handleSelectSpaceToAdd,
    handleCloseAddSpaceDialog,
    handleAddSpaceAccess,
  } = useUserProfile({ org, userMrn });

  setDocumentTitle([user?.name || "User Profile", org.name]);

  const breadcrumbs = [
    <Link
      key="/organization/overview"
      component={RouterLink}
      to={`/organization/overview?organizationId=${org.id}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Link
      key="/organization/users"
      component={RouterLink}
      to={`/organization/users?organizationId=${org.id}`}
    >
      Users
    </Link>,
    <Typography key="user-profile">{user?.name || "User Profile"}</Typography>,
  ];

  if (loading) {
    return <UserProfileSkeleton breadcrumbs={breadcrumbs} />;
  }

  if (!user) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3, overflowWrap: "anywhere" }} separator="›">
          {breadcrumbs}
        </Breadcrumbs>
        <Typography color="error">User not found</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3, overflowWrap: "anywhere" }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>

      {/* Back link */}
      <Link
        component={RouterLink}
        to={`/organization/users?organizationId=${org.id}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mb: 2,
          textDecoration: "none",
          color: "primary.main",
          fontWeight: 600,
          fontSize: "0.875rem",
          textTransform: "uppercase",
        }}
      >
        Back to Users
      </Link>

      {/* User header with avatar, name, and remove button */}
      <UserHeader
        user={user}
        onRemoveUser={handleRemoveUser}
        canRemove={canRemoveMembership}
      />

      {/* User profile card with name, email, and role */}
      <UserProfileCard
        user={user}
        onEditRole={handleEditUsers}
        canEditRole={canSetMembership}
      />

      {/* Space access table */}
      <SpaceAccessTable
        spaces={filteredSpaces}
        searchValue={spaceSearch}
        onSearchChange={setSpaceSearch}
        onManageAccess={handleOpenEditSpaceRole}
        paginationModel={spacesPaginationModel}
        onPaginationModelChange={onSpacesPaginationModelChange}
        rowCount={spacesRowCount}
      />

      {/* Remove user confirmation dialog */}
      <RemoveUserDialog
        isOpen={isRemoveDialogOpen}
        userName={user.name}
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
      />

      {/* Edit users dialog */}
      {user && (
        <EditUsersDialog
          open={isEditUsersDialogOpen}
          organizationRole={user.organizationRole}
          orgMrn={org.mrn}
          spaces={user.spaces.map((space) => ({
            mrn: space.mrn,
            name: space.name,
            assetCount: space.assetCount,
          }))}
          onClose={handleCancelEditUsers}
          onSave={handleSaveEditUsers}
          onEditRole={handleEditOrgRole}
          onAddSpace={handleSelectSpaceToAdd}
          isLoading={isEditRoleLoading}
        />
      )}

      {/* Edit organizational role dialog (nested) */}
      {user && (
        <EditOrganizationalRoleDialog
          open={isEditOrgRoleDialogOpen}
          currentRoles={[user.organizationRole]}
          onClose={handleCancelEditOrgRole}
          onSave={handleSaveOrgRole}
          isLoading={isEditRoleLoading}
        />
      )}

      {/* Edit space role dialog */}
      {editSpaceRoleDialog.space && (
        <EditSpaceRoleDialog
          open={editSpaceRoleDialog.open}
          spaceName={editSpaceRoleDialog.space.name}
          spaceMrn={editSpaceRoleDialog.space.mrn}
          currentRoles={editSpaceRoleDialog.space.roles}
          onClose={handleCloseEditSpaceRole}
          onSave={handleSaveSpaceRole}
          onRemoveAccess={handleRemoveSpaceAccess}
          isLoading={isSaveSpaceRoleLoading}
        />
      )}

      {/* Add space role dialog */}
      {addSpaceDialog.space && (
        <AddSpaceRoleDialog
          open={addSpaceDialog.open}
          spaceName={addSpaceDialog.space.name}
          spaceMrn={addSpaceDialog.space.mrn}
          onClose={handleCloseAddSpaceDialog}
          onSave={handleAddSpaceAccess}
          isLoading={isSaveSpaceRoleLoading}
        />
      )}
    </Box>
  );
}
